import React from 'react';
import { Alert, Box, Stack, Tooltip, Typography, alpha } from '@mui/material';
import SalesOpsHeader from './SalesOpsHeader';
import {
  BarListSkeleton,
  Card,
  MethodNote,
  Shimmer,
  StatBar,
  TableSkeleton,
  Tile,
  count,
  moneyShort,
  num,
  pct,
  tableSx,
} from './salesOpsKit';
import { fetchSalesOpsPlan } from '../../services/salesOperationsService';
import type { SalesOpsHeatCell, SalesOpsPlan } from '../../types/salesOperations';

/**
 * Plan -- capacity to target (handover 2.4). The screen a workforce-management
 * person recognises immediately.
 *
 * The headline comparison is required FTE against scheduled FTE, and the gap
 * is priced in commission, because the person who approves headcount thinks
 * in money rather than in contacts.
 *
 * The heatmap is the capacity argument made visible: connect rate is a
 * property of the LEAD's clock, coverage is a property of the AGENT's clock,
 * and where those disagree there are hours going to waste.
 */
export default function SalesOpsPlanPage() {
  const [data, setData] = React.useState<SalesOpsPlan | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback((force = false) => {
    const controller = new AbortController();
    setLoading(true);
    fetchSalesOpsPlan(controller.signal, force)
      .then((d) => { setData(d); setError(null); })
      .catch((e: Error) => { if (e.name !== 'AbortError') setError(e.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  React.useEffect(() => load(), [load]);

  const c = data?.capacity;
  const pending = loading || !data;
  const shortfall =
    c && c.required_fte !== null && c.scheduled_fte !== null
      ? c.required_fte - c.scheduled_fte
      : null;

  return (
    <Box>
      <SalesOpsHeader
        title="Plan"
        description="Capacity against this month&#39;s target, and where the hours are going to waste"
        asOf={data?.anchor_date}
        computedAt={data?.computed_at}
        fromCache={data?.from_cache}
        onRefresh={() => load(true)}
        refreshing={loading}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!error && (
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.75,
            }}
          >
            <Tile loading={pending} label="Target" value={count(c?.deal_target)} detail="policies this month" />
            <Tile
              loading={pending}
              label="Projected"
              value={count(c?.projected_policies)}
              detail={`${pct(c?.attainment, 0)} of target`}
              tone={c && c.attainment !== null && c.attainment < 1 ? 'warn' : 'good'}
              tooltip="Straight-line from month-to-date. Deliberately naive — a seasonality model here would be harder to argue with than it deserves."
            />
            <Tile
              loading={pending}
              label="FTE required"
              value={num(c?.required_fte)}
              detail={`${num(c?.scheduled_fte)} scheduled`}
              tone={shortfall !== null && shortfall > 0 ? 'warn' : 'neutral'}
            />
            <Tile
              loading={pending}
              label="Gap in commission"
              value={moneyShort(c?.gap_commission)}
              detail="at the current run rate"
              tooltip="Policies short of target, priced at the trailing-quarter average NET commission per policy — net of chargebacks, because gross would overstate it by whatever share lapses."
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Card title="Capacity to target">
              {pending ? <BarListSkeleton rows={2} /> : (
              <Stack spacing={2}>
                <StatBar
                  label="Scheduled FTE"
                  value={c!.scheduled_fte}
                  display={num(c!.scheduled_fte)}
                  max={c!.required_fte ?? 0}
                  tone="accent"
                />
                <StatBar
                  label="Required to hit target"
                  value={c!.required_fte}
                  display={num(c!.required_fte)}
                  max={c!.required_fte ?? 0}
                  tone="warn"
                />
                {shortfall !== null && shortfall > 0 && (
                  <Box
                    sx={(t) => ({
                      pt: 1.5,
                      borderTop: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}`,
                      fontSize: 13,
                      color: 'text.secondary',
                    })}
                  >
                    Closing the gap with people means{' '}
                    <strong style={{ color: 'inherit' }}>{num(shortfall)} more FTE</strong>.
                    The alternative is spending the hours differently — the heatmap below
                    shows where they currently go.
                  </Box>
                )}
              </Stack>
              )}
            </Card>

            <Card title="How required FTE is derived">
              {pending ? <TableSkeleton rows={7} cols={2} /> : (
              <Box component="table" sx={tableSx}>
                <tbody>
                  <DerivationRow label="Policies still needed"
                    value={count((c!.deal_target ?? 0) - (c!.policies_mtd ?? 0))} />
                  <DerivationRow label="Lead-to-policy rate" value={pct(c!.lead_to_policy_rate, 2)} />
                  <DerivationRow label="Leads needed" value={count(c!.leads_needed)} />
                  <DerivationRow label="Attempts per lead" value={num(c!.attempts_per_lead)} />
                  <DerivationRow label="Attempts needed" value={count(c!.attempts_needed)} />
                  <DerivationRow label="Dials per agent-hour" value={num(c!.dials_per_agent_hour)} />
                  <DerivationRow label="Leads arriving per day" value={count(c!.leads_per_day)} />
                </tbody>
              </Box>
              )}
              {pending
                ? <Shimmer width="90%" height={12} sx={{ mt: 1.5 }} />
                : <MethodNote>{data!.derivation}</MethodNote>}
            </Card>
          </Box>

          <Card title="Coverage against connect rate — where the hours are going to waste">
            {pending ? <Shimmer height={200} /> : <Heatmap cells={data!.heatmap} />}
            <MethodNote>
              Connect rate is measured on the lead&apos;s own clock; coverage counts agents
              whose shift overlaps that hour once their timezone is accounted for. A
              dashed cell connects above average and is staffed below the median — a
              good hour with nobody on it.
            </MethodNote>
          </Card>
        </Stack>
      )}
    </Box>
  );
}

function DerivationRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      component="tr"
      sx={(t) => ({ borderTop: `1px solid ${t.tokens?.border?.subtle ?? t.palette.divider}` })}
    >
      <td style={{ color: 'var(--mui-palette-text-secondary)' }}>{label}</td>
      <td className="num"><strong>{value}</strong></td>
    </Box>
  );
}

/** State x lead-local-hour grid, shaded by connect rate. */
function Heatmap({ cells }: { cells: SalesOpsHeatCell[] }) {
  const states = Array.from(new Set(cells.map((c) => c.state_cd)));
  const hours = Array.from(new Set(cells.map((c) => c.hour))).sort((a, b) => a - b);
  const byKey = new Map(cells.map((c) => [`${c.state_cd}|${c.hour}`, c]));
  const rates = cells.map((c) => c.connect_rate);
  const maxRate = Math.max(...rates, 0.0001);

  if (cells.length === 0) {
    return <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>No coverage data.</Typography>;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', fontSize: 11.5, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 90 }} />
            {hours.map((h) => (
              <Box
                component="th"
                key={h}
                sx={{ p: 0.75, fontSize: 10.5, color: 'text.disabled', fontWeight: 400 }}
              >
                {h % 12 || 12}{h < 12 ? 'a' : 'p'}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {states.map((s) => (
            <tr key={s}>
              <Box
                component="td"
                sx={{ px: 1.5, py: 0.75, fontWeight: 500, whiteSpace: 'nowrap', fontSize: 12 }}
              >
                {s}
              </Box>
              {hours.map((h) => {
                const cell = byKey.get(`${s}|${h}`);
                return (
                  <Box component="td" key={h} sx={{ p: 0.25 }}>
                    <Tooltip
                      arrow
                      title={
                        cell
                          ? `${s} · ${cell.hour_label} · ${pct(cell.connect_rate, 2)} connect ` +
                            `on ${count(cell.dials)} dials · ${cell.agents_scheduled} agents scheduled`
                          : 'No data'
                      }
                    >
                      <Box
                        sx={(t) => ({
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 0.5,
                          fontSize: 11,
                          bgcolor: cell
                            ? alpha(t.palette.primary.main, 0.08 + 0.62 * (cell.connect_rate / maxRate))
                            : 'transparent',
                          color: cell && cell.connect_rate / maxRate > 0.55
                            ? t.palette.primary.contrastText
                            : t.palette.text.primary,
                          outline: cell?.under_covered
                            ? `1.5px dashed ${t.palette.warning.main}`
                            : 'none',
                          outlineOffset: '-2px',
                        })}
                      >
                        {cell ? (cell.connect_rate * 100).toFixed(1) : ''}
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}
